import React, { useEffect, useState } from 'react'
import api from 'js/api'
import { getSessionItem, setSessionItem } from 'js/storeManager'
import { useSelector } from 'react-redux'
import { selectIsLoggedIn } from '../../store/userSlice'
import Spinner from 'js/components/Spinner/Spinner'
import GetStarted from './GetStarted'
import Success from './Success'
import {
  alreadyPremiumError,
  invalidTokenError,
  unhandledError,
  noTokenError,
} from './errors'
import Error from './Error'
import { useSearchParams } from 'react-router-dom'
import { captureException } from 'js/utils/captureException'

const Loading = () => (
  <Spinner
    width={35}
    color="green"
    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
  />
)

type Props = {
  onLoginClick: () => void
}

export default function TopArea({ onLoginClick }: Props) {
  const [searchParams] = useSearchParams()
  const [loadingInfo, setLoadingInfo] = useState(true)
  const [error, setError] = useState<
    { heading: React.ReactNode; text: React.ReactNode } | false
  >(false)
  const loggedIn = useSelector(selectIsLoggedIn)
  const [done, setDone] = useState(!!getSessionItem('gympassDone'))
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token || done) {
      setLoadingInfo(false)
      return
    }

    api('GET', `gympass-users/v1/info/${token}`)
      .then(({ first_name: firstName, last_name: lastName, email, token }) => {
        const obj = { firstName, lastName, email, token }
        setSessionItem('gympassUserInfo', JSON.stringify(obj))
        setLoadingInfo(false)
      })
      .catch((error) => {
        setLoadingInfo(false)
        setError(invalidTokenError)
        captureException(error, {
          context: {
            tags: {
              apiCall: 'gympass-users/v1/info/<token>',
              token,
            },
          },
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    if (
      !loadingInfo &&
      loggedIn &&
      !done &&
      getSessionItem('gympassUserInfo')
    ) {
      api('POST', 'gympass-users/v1/claim_premium', {
        token: JSON.parse(getSessionItem('gympassUserInfo') || '{}')?.token,
      })
        .then(() => {
          setSessionItem('gympassDone', true)
          setDone(true)
        })
        .catch((error) => {
          if (error?.current_subscription?.store) {
            setError(alreadyPremiumError(error.current_subscription.store))
          } else {
            setError(unhandledError)
            captureException(error, {
              context: {
                tags: {
                  apiCall: 'gympass-users/v1/claim_premium',
                  token:
                    JSON.parse(getSessionItem('gympassUserInfo') || '{}')
                      ?.token || 'no-token',
                  gympassDone:
                    getSessionItem('gympassDone') ||
                    'no-gympassDone in sessionStorage',
                  gympassUserInfo:
                    getSessionItem('gympassUserInfo') ||
                    'no-gympassUserInfo in sessionStorage',
                  stateVariables: JSON.stringify({
                    loadingInfo,
                    error,
                    token,
                    loggedIn,
                    gympassDone: getSessionItem('gympassDone'),
                    gympassUserInfo: getSessionItem('gympassUserInfo'),
                    done,
                  }),
                },
              },
            })
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingInfo, loggedIn, done])

  if (loadingInfo) return <Loading />

  if (error) return <Error heading={error.heading}>{error.text}</Error>

  if (!token && !getSessionItem('gympassUserInfo'))
    return <Error heading={noTokenError.heading}>{noTokenError.text}</Error>

  if (!getSessionItem('gympassUserInfo')) return <Loading />

  if (!loggedIn) {
    return (
      <GetStarted
        onLoginClick={onLoginClick}
        firstName={
          JSON.parse(getSessionItem('gympassUserInfo') || '{}')?.firstName
        }
      />
    )
  }

  if (!done) return <Loading />

  return <Success />
}
